import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  configReturnStatusesApi,
  type CreateReturnStatusPayload,
  type ReturnStatus,
  type UpdateReturnStatusPayload,
} from '@/services/api/config-return-statuses';

export const useConfigReturnStatuses = () => {
  return useQuery({
    queryKey: ['configReturnStatuses'],
    queryFn: () => configReturnStatusesApi.listStatuses(),
  });
};

export const useCreateReturnStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateReturnStatusPayload) =>
      configReturnStatusesApi.createStatus(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configReturnStatuses'] });
    },
  });
};

export const useUpdateReturnStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: UpdateReturnStatusPayload }) =>
      configReturnStatusesApi.updateStatus(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configReturnStatuses'] });
    },
  });
};

export const useDeleteReturnStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => configReturnStatusesApi.deleteStatus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configReturnStatuses'] });
    },
  });
};

export const useTranslateStatusBatch = () => {
  return useMutation({
    mutationFn: ({ text, targetLangs }: { text: string; targetLangs: string[] }) =>
      configReturnStatusesApi.translateBatch(text, targetLangs),
  });
};

export const useReorderReturnStatuses = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (orderedIds: number[]) =>
      configReturnStatusesApi.reorderStatuses({ ordered_ids: orderedIds }),
    onMutate: async (orderedIds) => {
      await queryClient.cancelQueries({ queryKey: ['configReturnStatuses'] });

      const previous = queryClient.getQueryData<ReturnStatus[]>(['configReturnStatuses']);

      if (previous) {
        const reordered = orderedIds
          .map((id, index) => {
            const status = previous.find((s) => s.id === id);
            return status ? { ...status, sort_order: index } : null;
          })
          .filter(Boolean) as ReturnStatus[];

        queryClient.setQueryData(['configReturnStatuses'], reordered);
      }

      return { previous };
    },
    onError: (_err, _orderedIds, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['configReturnStatuses'], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['configReturnStatuses'] });
    },
  });
};
